<template>
  <modal
    :visible="active"
    class="quickview-modal"
    @on-close="close"
  >
    <ui-loader
      v-if="loading"
      class="quickview-modal-loader"
      size="64"
    />

    <div class="quickview">
      <div class="quickview-gallery">
        <swiper
          :items="images"
          v-slot="{ item }"
          :slides-to-show="1"
          dots
          arrows
          @change="onChangeSlide"
        >
          <media :media="item" width="400" :alt="name" />
        </swiper>

        <favorite-button
          v-if="product.id"
          :product-id="product.id"
          :product-code="product.code"
          :product-price="currentPrice"
          :sku-id="activeSku.id"
          @change="onChangeFavorite"
        />
      </div>

      <div class="quickview-content">
        <product-markers
          :markers="product.markers"
          :plates="product.plates"
        />

        <h1 class="quickview-content-title">
          {{ name }}
          <span>{{ product.code }}</span>
        </h1>

        <!-- прячем контент при первом открытии -->
        <template v-if="product.id">
          <div class="quickview-content-prices">
            <div :class="['price price-rub', {discount: isDiscount}]">
              {{ currentPrice | price }}
            </div>
            <div
              v-if="isDiscount"
              class="old-price"
            >
              {{ product.price.basePrice | price }}
            </div>
          </div>

          <select-color
            show-color-name
            label="Цвета"
            :colors="colors"
            :active-color="product.id"
            :colors-limit="10"
            @change="chooseColor"
          />

          <select-size
            visible-hint
            label="Размеры"
            :sizes="sizes"
            :active-sku="activeSku"
            @change="chooseSize"
          >
            <size-table
              v-if="product.sizeTable"
              :product-id="product.id"
              @open="onOpenSizeTable"
            />
          </select-size>

          <to-cart
            :product="product"
            :sku-id="activeSku.id"
            @add="onAddToCart"
            @added="active = false"
            @to-cart="onRedirectToCart"
          >
            <ui-button
              ref="detailsBtn"
              class="details"
              secondary
              wide
              text="Подробнее"
              @click="onClickDetails"
            />
          </to-cart>

          <ui-notification
            class="sold-out"
            v-if="!product.available"
            title="Товар распродан"
          />

          <product-delivery
            :active-sku="activeSku"
            :availability-stores="availabilityStores"
            class="quickview__product-delivery"
            @click="onClickProductDelivery"
          />

          <product-bonus :bonus="product.bonus" />
        </template>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';

/* Components */
import ProductMarkers from './product-markers.vue';
import selectSize from './select-size.vue';
import SelectColor from './select-color.vue';
import ToCart from './to-cart.vue';
import UiLoader from '../kit/ui-loader/ui-loader.vue';
import ProductDelivery from '@/components/product/product-delivery.vue';
import ProductBonus from './product-bonus.vue';
import SizeTable from './size-table.vue';
import FavoriteButton from '../favorite-button/favorite-button.vue';
import { productGtmEvents } from '@/gtm/product';
import { DELIVERY_TYPES } from '@/constants/delivery';

export default {
  name: 'quickview',

  components: {
    ProductMarkers,
    selectSize,
    SelectColor,
    ToCart,
    UiLoader,
    ProductDelivery,
    ProductBonus,
    SizeTable,
    FavoriteButton,
  },

  data: () => ({
    sku: '',
    model: {},
    product: {},
    colors: [],
    active: false,
    loading: false,
    availabilityStores: [],
  }),

  computed: {
    ...mapGetters({
      CITY: 'user/city',
    }),

    activeSku() {
      return this.sku
        ? this.product?.skus?.find(sku => sku.available && sku.id === this.sku) || {}
        : {}
      ;
    },

    images() {
      return (this.product.media || this.model.media)?.filter(({ type }) => type === 'photo') || [];
    },

    name() {
      return this.product.name || this.model.name;
    },

    sizes() {
      return this.product.skus || [];
    },

    currentPrice() {
      return this.product.price?.discountPrice ||
          this.product.price?.basePrice;
    },

    isDiscount() {
      return Number(this.product.price?.discountRate);
    },
  },

  created() {
    if (this.$isServer) return;
    this.$f.$on('quickview', this.init);
  },

  methods: {
    async init(model) {
      this.active = true;
      if (model.id === this.product.id) {
        this.onShowProductDelivery();
        return;
      }
      this.model = model;
      this.product = {};
      await this.loadProduct(model.id);
      this.setColors();
    },

    async loadProduct(productId) {
      this.loading = true;

      try {
        const [product, availabilityStores] = await Promise.all([
          this.$store.$api.loadProduct({ productId }),
          this.$store.$api.loadAvailabilityList({ productId })
        ]);

        this.product = product;
        this.availabilityStores = availabilityStores;
      } catch (e) {
        this.$store.dispatch('notifications/create', {
          title: 'Ошибка',
          message: 'Не удалось получить информацию о товаре. Попробуйте позже.',
        });
      } finally {
        this.loading = false;
      }
    },

    setColors() {
      // чтобы не сбивался порядок
      this.colors = this.product.models?.map(m => ({
        modelId: m.id,
        ...m.color
      })) || [];
    },

    async chooseColor(id) {
      await this.loadProduct(id);
      productGtmEvents.quickView.chooseColor({ productCode: this.product?.code });
      this.onShowProductDelivery();
    },

    chooseSize({ id, size }) {
      this.sku = id;

      productGtmEvents.quickView.chooseSize({
        productCode: this.product?.code,
        size,
      });

      this.onShowProductDelivery();
    },

    close() {
      this.active = false;
    },

    onClickProductDelivery({ name, deliveryInterval, storesCount }) {
      productGtmEvents.quickView.chooseDelivery({
        productCode: this.product?.code,
        cityName: this.CITY?.name,
        size: this.activeSku?.size,
        deliveryType: name,
        deliveryInterval,
        storesCount,
      });

      this.openFullcard(name === DELIVERY_TYPES.postamat ? DELIVERY_TYPES.delivery : name);
    },

    onShowProductDelivery() {
      productGtmEvents.quickView.showDelivery({
        productCode: this.product?.code,
        cityName: this.CITY?.name,
        activeSku: this.activeSku,
      });
    },

    openFullcard(hash = '') {
      window.location = `/product/${this.product.id}`
          + `${this.activeSku?.id ? '?sku-id=' + this.activeSku.id : ''}`
          + `${typeof hash !== 'object' ? '#' + hash : ''}`;
    },

    onAddToCart() {
      productGtmEvents.quickView.addToCart({
        productCode: this.product?.code,
      });
    },

    onRedirectToCart() {
      productGtmEvents.quickView.redirectToCart();
    },

    onChangeFavorite(value) {
      productGtmEvents.quickView.toggleFavorite({
        value,
        productCode: this.product?.code,
      });
    },

    onClickDetails() {
      productGtmEvents.quickView.clickDetails({ productCode: this.product?.code });

      this.openFullcard();
    },

    onOpenSizeTable() {
      productGtmEvents.quickView.openSizeTable({ productCode: this.product?.code });
    },

    onChangeSlide() {
      productGtmEvents.quickView.changeSlide({ productCode: this.product?.code });
    },
  },
}
</script>

<style lang="scss">
.quickview-modal {
  & > .modal-content {
    padding: 0;
  }

  &-loader {
    z-index: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    color: $blue;
    ~ * {
      opacity: .6;
      pointer-events: none;
    }
  }
}

.quickview {
  display: flex;
  width: 878px;
  min-height: 613px;
  overflow: hidden;

  &-gallery {
    position: relative;
    width: 439px;

    .swiper {
      height: 100%;

      &-wrapper {
        height: 100%;
        margin: 0;
      }

      .dots {
        position: absolute;
        bottom: 10px;
        width: 100%;
      }

      .arrows {
        left: 12px;
        right: 12px;
        top: 46%;
      }

      &-slides {
        padding: 0;
      }

      &-slide {
        padding: 0;
      }
    }

    .favorite-button {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 40px;
      height: 40px;
    }
  }

  &-content {
    flex: 1;
    position: relative;
    padding: 12px 24px;

    .product-markers {
      margin: -6px -5px;
    }

    &-title {
      margin-top: 10px;
      font-weight: 300;
      font-size: 24px;
      line-height: 32px;

      span {
        font-family: Gilroy, sans-serif;
        font-size: 14px;
        line-height: 20px;
        text-align: right;
        color: $gray;
      }
    }

    .select-color {
      // тут не как в основной карточе
      &-label {
        margin: 4px 0;
      }

      &-name {
        font-size: 12px;
      }

      li {
        padding: 4px;

        &.active {
          box-shadow: inset 0 0 0 1px $black;
        }
      }
    }

    .select-size {
      li {
        padding: 6px 8px;
        min-width: 32px;
      }

      &-hint {
        margin-top: 12px;
      }

      &-label {
        margin-top: 16px;
      }
    }

    .select-color-label,
    .select-size-label {
      font-size: 14px;
      line-height: 16px;
      color: $gray;
    }

    .to-cart {
      display: flex;
      flex-wrap: wrap;
      margin-top: 24px;

      &__button {
        width: 257px;
      }
    }

    .details {
      width: 126px;
      margin-left: 8px;
      font-size: 16px;
    }

    .sold-out {
      margin-top: 24px;
    }

    .product-bonus {
      margin-top: 12px;
    }
  }

  .quickview-content-prices {
    display: flex;
    align-items: center;
    font-size: 40px;
    line-height: 48px;

    .price {
      display: flex;
      font-weight: 600;
      font-size: 32px;
      line-height: 40px;

      &.discount {
        color: $red;
      }
    }

    .old-price {
      margin-left: 12px;
      color: $gray;
      font-weight: normal;
      font-size: 20px;
      line-height: 24px;
      text-decoration: line-through;
      transform: translateY(6px);
    }
  }

  &__product-delivery {
    margin-top: 12px;

    .product-delivery-item {
      padding: 12px;
      font-size: 12px;
      line-height: 16px;

      &__title {
        margin-bottom: 0;
        font-size: 14px;
      }

      &__icon {
        width: 40px;
        height: 40px;

        .sprite-icon {
          height: 20px;
          width: 20px;
        }
      }
    }
  }
}
</style>
